import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { canliModulDurumu } from "./api";

/**
 * CANLI ÇALIŞMA İZLEME — ÖĞRENCİ/VELİ TARAFI (F-LIV-07)
 *
 * ================================================================
 * MODÜL DURUMU UYGULAMA DÜZEYİNDE BİR KEZ OKUNUR
 * ================================================================
 * Görev kartı günde onlarca kez çiziliyor; her kartın kendi isteğini atması hem boşuna yük hem de
 * kartlar arası tutarsızlık demek (biri not gösterir, öteki göstermez). Durum bir kez, `Uygulama`da
 * okunur ve bağlamla kartlara iner. Bağlam yoksa (kart tek başına çizildiğinde) varsayılan KAPALI:
 * bağlamı kuran `Uygulama`dır ve orada durum bilinmiyorsa not GÖSTERİLİR (aşağıdaki kanca).
 *
 * Bu uygulama canlı LİSTE ucunu çağırmaz — öğrenci ve veli listeyi göremez, yalnız bilgilendirilir.
 */

/** Modül durumunun sorgu anahtarı — öğrenci kabuğu ve veli portalı aynı önbelleği paylaşır. */
export const CANLI_MODUL_ANAHTARI = ["canli-izleme-modul"] as const;

/** Modül durumu bu aralıkla yeniden okunur (yalnız sekme görünürken); pencere odağa dönünce de okunur. */
export const CANLI_MODUL_YENILEME_MS = 60_000;

/**
 * Son BAŞARILI okumanın üzerinden bu kadar geçtiyse "kapalı" yanıtı artık güncel sayılmaz (2,5 aralık).
 * Neden: yenileme sessizce askıda kalabilir (askıya alınmış sekme, takılı istek) ve eski `false`
 * notu sonsuza dek gizler; yönetici modülü açtığında öğrenci haberdar edilmeden izlenirdi.
 */
export const CANLI_MODUL_BAYATLIK_MS = 150_000;

/** Bayatlık denetiminin tık aralığı; yalnız durum DEĞİŞİNCE yeniden çizilir (kabuğu boşuna çizmez). */
const BAYATLIK_DENETIM_MS = 15_000;

/**
 * Not gösterilsin mi? YALNIZ modülün KAPALI olduğu TAZE ve BAŞARILI bir okumayla biliniyorsa `false`
 * (F-LIV-07).
 *
 * Emre kararı 08.10.2026 (koordinatör iletti): şeffaflık — "öğrenci bilsin". Modül varsayılan AÇIK;
 * durum okunamadığında (hata, çevrimdışı, henüz yükleniyor) izleme büyük olasılıkla sürüyor ve
 * söylememek yanlış taraf olurdu. Önce tersiydi (bilinmiyorsa gizle); o hâlde ağ hatası, izleme
 * sürerken notu kaldırıyordu.
 *
 * İKİ EK KURAL (S8-KOD-04): (1) Son okuma "kapalı" dese bile SONRAKİ okuma hata verdiyse not görünür:
 * TanStack Query hata sonrası eski `data`yı korur, o yüzden `data.acik === false` tek başına "kesin
 * kapalı" demek değil — `isError` ve yaş da bakılır. (2) Yönetici modülü sonradan açabilir: durum
 * `CANLI_MODUL_YENILEME_MS` aralığıyla ve odağa dönünce yenilenir; yoksa açık kalan uygulama eski
 * `false`la izlemeyi gizlerdi.
 *
 * `queryFn` bir ok işlevi: `canliModulDurumu` içe aktarması çizim sırasında değil istek anında
 * okunuyor, yani sahte (mock) bir `api` modülünde bu işlev tanımlı değilse çizim patlamıyor.
 */
export function useCanliModulAcik(etkin: boolean): boolean {
  const sorgu = useQuery({
    queryKey: CANLI_MODUL_ANAHTARI,
    queryFn: () => canliModulDurumu(),
    enabled: etkin,
    retry: false,
    staleTime: CANLI_MODUL_YENILEME_MS,
    refetchInterval: CANLI_MODUL_YENILEME_MS,
    // Sekme gizliyken boşuna sorgu atılmaz; görünür olunca odak yenilemesi zaten okur.
    refetchIntervalInBackground: false,
    // "always": bayatlık süresine bakmadan odağa dönüşte oku — uzun uykudan dönen telefon eski durumu göstermesin.
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    // Çevrimdışıyken istek bekletilmez, HATA verir: bekletilen istek eski `false`ı "başarılı" bırakırdı.
    networkMode: "always",
  });

  /*
    Bayatlık zamanla değişen bir değer: kendi kendine yeniden çizim tetiklenmeli. Durumda "hangi okumanın
    bayatladığı" tutuluyor (boolean değil): yeni okuma gelince `dataUpdatedAt` değişir ve bayatlık
    kendiliğinden düşer — etki çalışana dek geçen çizimde yeni veri eski bayatlıkla ezilmez.
  */
  const guncelleme = sorgu.dataUpdatedAt;
  const [bayatOkuma, setBayatOkuma] = useState(0);
  useEffect(() => {
    if (!etkin || guncelleme === 0) return;
    const denetle = () => {
      if (Date.now() - guncelleme > CANLI_MODUL_BAYATLIK_MS) setBayatOkuma(guncelleme);
    };
    denetle();
    const zamanlayici = setInterval(denetle, BAYATLIK_DENETIM_MS);
    document.addEventListener("visibilitychange", denetle);
    return () => {
      clearInterval(zamanlayici);
      document.removeEventListener("visibilitychange", denetle);
    };
  }, [etkin, guncelleme]);

  const bayat = guncelleme !== 0 && bayatOkuma === guncelleme;
  const kesinKapali = sorgu.data?.acik === false && !sorgu.isError && !bayat;
  return etkin && !kesinKapali;
}

export interface CanliIzlemeBaglamDegeri {
  /** Modül açık: görev kartı kısa notu ve bağlantıyı gösterebilir. */
  acik: boolean;
  /** Bilgilendirme sayfasını açar; yoksa "Ayrıntı" bağlantısı çizilmez. */
  bilgiAc: (() => void) | null;
}

export const CanliIzlemeBaglami = createContext<CanliIzlemeBaglamDegeri>({
  acik: false,
  bilgiAc: null,
});

export function useCanliIzleme(): CanliIzlemeBaglamDegeri {
  return useContext(CanliIzlemeBaglami);
}
